import { useEffect, useState, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import {
  useApi,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  parseEntityRef,
  stringifyEntityRef,
  type GroupEntity,
  type UserEntity,
} from '@backstage/catalog-model';

type Resolution =
  | { state: 'pending' }
  | { state: 'home' }
  | { state: 'redirect'; to: string };

export function MyTeamRedirect({ children }: { children: ReactElement }) {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [resolution, setResolution] = useState<Resolution>({ state: 'pending' });

  useEffect(() => {
    let cancelled = false;
    const resolve = async (): Promise<Resolution> => {
      const identity = await identityApi.getBackstageIdentity();
      const userRef = identity.userEntityRef;
      if (!userRef || userRef === 'user:default/guest') {
        return { state: 'home' };
      }

      const user = (await catalogApi.getEntityByRef(userRef)) as
        | UserEntity
        | undefined;
      // Prefer `relations` (authoritative — populated by the catalog from
      // processors/providers) and fall back to `spec.memberOf` when relations
      // are empty.
      const relationRefs =
        user?.relations
          ?.filter(r => r.type === 'memberOf')
          .map(r => r.targetRef) ?? [];
      const groupRefs =
        relationRefs.length > 0
          ? relationRefs
          : (user?.spec?.memberOf ?? []).map(name =>
              stringifyEntityRef(parseEntityRef(name, { defaultKind: 'Group' })),
            );
      if (groupRefs.length === 0) {
        return { state: 'home' };
      }
      const { items } = await catalogApi.getEntitiesByRefs({ entityRefs: groupRefs });
      // If any requested ref didn't resolve (catalog sync lag), bail out:
      // an unresolved ref could be a team we can't see, so membership is
      // ambiguous and we should not redirect.
      if (items.some(e => e === undefined)) {
        return { state: 'home' };
      }
      const teams = items.filter(
        (e): e is GroupEntity => e?.spec?.type === 'team',
      );
      if (teams.length !== 1) {
        return { state: 'home' };
      }

      const team = teams[0];
      const namespace = team.metadata.namespace ?? 'default';
      return {
        state: 'redirect',
        to: `/catalog/${namespace}/group/${team.metadata.name}`,
      };
    };

    resolve()
      .then(r => {
        if (!cancelled) setResolution(r);
      })
      .catch(() => {
        if (!cancelled) setResolution({ state: 'home' });
      });

    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi]);

  if (resolution.state === 'pending') {
    return <Progress />;
  }
  if (resolution.state === 'redirect') {
    return <Navigate to={resolution.to} replace />;
  }
  return children;
}
